import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonPage,
  IonRouterLink,
  NavContext
} from '@ionic/react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';

import { Avatar, Padding, Spacer, Tips } from '@/components/common';
import { DashboardHeader } from '@/components/dashboard';
import { Icon, Text } from '@/components/memo';
import {
  useAuth,
  useClinic,
  useDentist,
  useShare,
  useSubscription,
  useWeeklyReports
} from '@/contexts';
import { Dentist, softDeleteDentist } from '@/data/Dentists';
import { withProfileModals } from '@/hocs';
import { useToast } from '@/hooks';
import {
  HELP_URL,
  POPUP_IDENTIFIERS,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL
} from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { getInitials } from '@/utils/helpers/getInitials';
import { ROUTES } from '@/utils/routes';
import { BLACKLISTED_SHARE_CODES } from '@/utils/shares';
import { TABS } from '@/utils/tabs';
import { ProfileSettingItem } from '@/utils/types';
import { APP_VERSION } from '@/utils/version';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  analyticsOutline,
  banOutline,
  cardOutline,
  createOutline,
  diamondOutline,
  helpCircleOutline,
  keyOutline,
  languageOutline,
  logOutOutline,
  medkitOutline,
  schoolOutline,
  shieldCheckmarkOutline,
  swapHorizontalOutline,
  timeOutline,
  trashOutline
} from 'ionicons/icons';
import { useLocation } from 'react-router-dom';

import './Profile.css';

interface ProfileProps {
  modals: {
    name: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    weeklyReports: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    workingHours: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    services: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    insurance: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    access: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    patientVerification: {
      isOpen: boolean;
      onOpen: () => void;
      onClose: () => void;
    };
    specializations: {
      isOpen: boolean;
      onOpen: () => void;
      onClose: () => void;
    };
    language: { isOpen: boolean; onOpen: () => void; onClose: () => void };
  };
}

const Profile: React.FC<ProfileProps> = ({ modals }) => {
  const { user, signOut } = useAuth();
  const { selectedClinic: clinic, isClinic } = useClinic();
  const queryClient = useQueryClient();
  const { navigate } = useContext(NavContext);
  const {
    selectDentist,
    selectedDentist: dentist,
    updateProfileTipsMutation
  } = useDentist();
  const {
    share,
    onUpdateShareCode,
    onShare,
    loading: updatingShareCode
  } = useShare();
  const { manage: onManageSubscription } = useSubscription();
  const { latestStartDate: weeklyReportLatestStartDate } = useWeeklyReports();
  const displayToast = useToast();

  const isNativePlatform = Capacitor.isNativePlatform();

  const { search } = useLocation();
  const popupFromUrl = new URLSearchParams(search).get('popup');

  const [shareCode, setShareCode] = useState<string>(share?.code || '');
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  const page = useRef<HTMLElement | undefined>(undefined);

  const showAnalytics = useMemo(() => {
    return (
      dentist?.weekly_report_subscription !== 'none' ||
      !!weeklyReportLatestStartDate
    );
  }, [dentist?.weekly_report_subscription, weeklyReportLatestStartDate]);

  useEffect(() => {
    if (share) setShareCode(share.code);

    return () => {
      setShareCode('');
    };
  }, [share]);

  useEffect(() => {
    if (showAnalytics && popupFromUrl === POPUP_IDENTIFIERS.WEEKLY_REPORT) {
      modals.weeklyReports.onOpen();
    }
  }, [showAnalytics, popupFromUrl, modals.weeklyReports.onOpen]);

  const onSwitchDentist = useCallback(async () => {
    await selectDentist(null);
    navigate(ROUTES.SELECT_DENTIST);
  }, [selectDentist, navigate]);

  const onSignOut = useCallback(async () => {
    await selectDentist(null);
    signOut();
  }, [selectDentist, signOut]);

  const deleteDentistMutation = useMutation({
    mutationFn: async (dentistId: string) => {
      return await softDeleteDentist(dentistId);
    },
    onSuccess: async (_, dentistId) => {
      await selectDentist(null);
      navigate(ROUTES.SELECT_DENTIST);
      queryClient.setQueryData(
        ['dentists', clinic?.id, user?.id, isClinic],
        (oldDentists: Dentist[] | undefined) => {
          if (!oldDentists) return [];
          return oldDentists.filter((dentist) => dentist.id !== dentistId);
        }
      );
      displayToast({
        message: 'Profile deleted successfully.',
        duration: 3000,
        position: 'top'
      });
    },
    onError: () => {
      displayToast({
        message: 'Failed to delete profile. Please try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const onShareButtonClick = useCallback(async () => {
    if (isInputFocused || shareCode !== share?.code) {
      if (!shareCode) {
        displayToast({
          message: 'Custom link cannot be empty.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      } else {
        if (BLACKLISTED_SHARE_CODES.includes(shareCode)) {
          displayToast({
            message: 'Custom link cannot be a reserved word.',
            duration: 3000,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
          return;
        }

        await onUpdateShareCode(shareCode);
        setIsInputFocused(false);
      }
    } else {
      onShare();
    }
  }, [isInputFocused, shareCode, share, onUpdateShareCode, onShare]);

  const onDeleteProfile = async () => {
    const { value: confirmation, cancelled } = await Dialog.prompt({
      title: 'Delete Profile',
      message: 'This action cannot be undone. Please type "DELETE" to confirm.',
      inputPlaceholder: 'Type DELETE'
    });

    if (cancelled) {
      return;
    }

    if (confirmation === 'DELETE' && dentist?.id) {
      deleteDentistMutation.mutate(dentist.id);
      return;
    }

    displayToast({
      message: 'Invalid confirmation.',
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'tab-bar'
    });
  };

  const onCloseAccount = async () => {
    await Dialog.alert({
      title: 'Close Account',
      message:
        'To close your account, please contact our support team at hello@odonto.ph.'
    });
  };

  const onHelp = async () => {
    await Browser.open({ url: HELP_URL });
  };

  const profileSettingItems: ProfileSettingItem[] = [
    ...(isClinic
      ? [
          {
            icon: keyOutline,
            text: 'Manage Access',
            onClick: modals.access.onOpen
          }
        ]
      : []),
    {
      icon: medkitOutline,
      text: 'Manage Services',
      onClick: modals.services.onOpen
    },
    {
      icon: timeOutline,
      text: 'Manage Working Hours',
      onClick: modals.workingHours.onOpen
    },
    {
      icon: schoolOutline,
      text: 'Manage Specializations',
      onClick: modals.specializations.onOpen
    },
    {
      icon: cardOutline,
      text: 'Manage Insurance',
      onClick: modals.insurance.onOpen
    },
    {
      icon: swapHorizontalOutline,
      text: `Switch ${isClinic ? 'Dentist' : 'Clinic'}`,
      onClick: onSwitchDentist
    },
    {
      icon: helpCircleOutline,
      text: 'Help',
      onClick: onHelp
    },
    {
      icon: logOutOutline,
      text: 'Sign Out',
      onClick: onSignOut
    }
  ];

  const bookingPageSettingItems: ProfileSettingItem[] = [
    ...(isClinic
      ? [
          {
            icon: languageOutline,
            text: 'Language',
            onClick: modals.language.onOpen
          }
        ]
      : []),
    {
      icon: shieldCheckmarkOutline,
      text: 'Patient Verification',
      onClick: modals.patientVerification.onOpen
    }
  ];

  const tips = [
    'Customize your booking link to reflect your brand.',
    <>
      To request a personalized QR code, just{' '}
      <IonRouterLink onClick={onHelp}>send us a message</IonRouterLink>.
    </>,
    'Boost bookings by sharing your link on your Facebook page.'
  ];

  const onHideTips = async () => {
    if (dentist?.id) {
      updateProfileTipsMutation.mutate(dentist.id);
    }
  };

  return (
    <IonPage id="profile" ref={page}>
      <IonContent>
        <Padding>
          <div className="profile__fixed-header">
            <DashboardHeader title="Profile" tab={TABS.PROFILE} />
            <Spacer top={4} bottom={16} />
          </div>
          <Spacer top={114} bottom={62}>
            <div className="profile__dentist">
              <div className="profile__dentist-accent"></div>
              <div className="profile__dentist-content">
                <Avatar
                  color="primary"
                  initials={getInitials(dentist)}
                  className="profile__dentist-avatar"
                />
                <Spacer top={12} />
                <div className="profile__dentist-name">
                  <div className="profile__dentist-name-text">
                    {formatDentistName({
                      dentist,
                      returnClinicFirstName: true
                    })}{' '}
                    <Text color="primary">
                      {formatDentistName({
                        dentist,
                        returnDentistLastName: true,
                        withHonorific: false,
                        returnClinicLastName: true
                      })}
                    </Text>
                  </div>
                  <Icon
                    icon={createOutline}
                    className="profile__dentist-name-edit-icon"
                    onClick={modals.name.onOpen}
                  />
                </div>
                {dentist?.clinic?.name && (
                  <p className="profile__clinic-name">
                    {dentist?.clinic?.name}
                  </p>
                )}
              </div>
            </div>
          </Spacer>
          <div id="booking-link" className="profile__section">
            <h3 id="booking-link-title" className="profile__section-title">
              Booking Link
            </h3>
            <div className="profile__share-link">
              <IonInput
                value={shareCode}
                placeholder="Set your custom link"
                onFocus={() => {
                  setIsInputFocused(true);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setIsInputFocused(false);
                  }, 1000);
                }}
                onIonInput={(e: CustomEvent) => setShareCode(e.detail.value!)}
              >
                <div slot="start" className="profile__share-link-prefix">
                  odonto.ph/
                </div>
              </IonInput>
              <IonButton
                onClick={onShareButtonClick}
                expand="block"
                fill="solid"
                size="small"
                color="primary"
                strong={true}
              >
                {isInputFocused || shareCode !== share?.code
                  ? share
                    ? 'Update'
                    : 'Create'
                  : 'Share'}
              </IonButton>
            </div>
            {dentist?.show_profile_tips && (
              <Tips content={tips} onHide={onHideTips} />
            )}
          </div>
          <Spacer top={42} />
          {showAnalytics && (
            <div id="analytics" className="profile__section">
              <h3 id="analytics-title" className="profile__section-title">
                Analytics
              </h3>
              <Spacer top={8} bottom={42}>
                <div className="profile__analytics-items">
                  <div
                    className="profile__analytics-item"
                    onClick={modals.weeklyReports.onOpen}
                  >
                    <Icon icon={analyticsOutline} />
                    <p>View Weekly Reports</p>
                  </div>
                </div>
              </Spacer>
            </div>
          )}
          <div id="profile-settings" className="profile__section">
            <h3 id="profile-settings-title" className="profile__section-title">
              Account Settings
            </h3>
            <Spacer top={8} bottom={42}>
              <div className="profile__profile-setting-items">
                {profileSettingItems.map((item, index) => (
                  <div
                    key={index}
                    className="profile__profile-setting-item"
                    onClick={item.onClick}
                  >
                    <Icon icon={item.icon} />
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </Spacer>
          </div>
          <div id="booking-page-settings" className="profile__section">
            <h3
              id="booking-page-settings-title"
              className="profile__section-title"
            >
              Booking Page Settings
            </h3>
            <Spacer top={8} bottom={42}>
              <div className="profile__booking-page-setting-items">
                {bookingPageSettingItems.map((item, index) => (
                  <div
                    key={index}
                    className="profile__profile-setting-item"
                    onClick={item.onClick}
                  >
                    <Icon icon={item.icon} />
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </Spacer>
          </div>
          {isClinic && isNativePlatform && (
            <div id="subscription-settings" className="profile__section">
              <h3
                id="subscription-settings-title"
                className="profile__section-title"
              >
                Subscription Settings
              </h3>
              <Spacer top={8} bottom={42}>
                <div id="subscription-setting-items">
                  <div
                    className="profile__profile-setting-item"
                    onClick={onManageSubscription}
                  >
                    <Icon icon={diamondOutline} />
                    <p>Manage Subscription</p>
                  </div>
                </div>
              </Spacer>
            </div>
          )}
          {isClinic && (
            <div id="danger-zone-settings" className="profile__section">
              <h3
                id="danger-zone-settings-title"
                className="profile__section-title"
              >
                Danger Zone
              </h3>
              <Spacer top={8} bottom={42}>
                <div className="profile__danger-zone-setting-items">
                  <div
                    className="profile__profile-setting-item"
                    onClick={() => onDeleteProfile()}
                  >
                    <Icon icon={trashOutline} className="danger" />
                    <p>Delete Profile</p>
                  </div>
                  <div
                    className="profile__profile-setting-item"
                    onClick={() => onCloseAccount()}
                  >
                    <Icon icon={banOutline} className="danger" />
                    <p>Close Account</p>
                  </div>
                </div>
              </Spacer>
            </div>
          )}
          <Spacer top={24} bottom={8}>
            <div className="profile__fineprint">
              <div className="profile__legal">
                <IonRouterLink
                  color="medium"
                  onClick={() => {
                    Browser.open({ url: TERMS_OF_SERVICE_URL });
                  }}
                >
                  Terms of Service
                </IonRouterLink>
                •
                <IonRouterLink
                  color="medium"
                  onClick={() => {
                    Browser.open({ url: PRIVACY_POLICY_URL });
                  }}
                >
                  Privacy Policy
                </IonRouterLink>
              </div>
              <Text id="app-version">App Version {APP_VERSION}</Text>
            </div>
          </Spacer>

          <IonLoading
            isOpen={
              updatingShareCode ||
              deleteDentistMutation.isPending ||
              updateProfileTipsMutation.isPending
            }
            message="Loading..."
            spinner="crescent"
          />
        </Padding>
      </IonContent>
    </IonPage>
  );
};

export default withProfileModals(Profile);
